/**
 * Lead Routing Logic
 *
 * Routes survey leads to the appropriate contact:
 * - **Randy Nemeroff** (Hero Benefits Alliance):
 *   Pre-approved or financially ready leads (homebuyers).
 * - **Randy Nemeroff – Refi** (same contact, refinance context):
 *   Homeowners exploring refinancing options.
 * - **Gordon Oliver** (Cambridge Credit Counseling):
 *   Not yet pre-approved and/or concerned about credit/finances.
 */

export interface RoutingContact {
  key: "randy_nemeroff" | "gordon_oliver" | "randy_refi";
  name: string;
  title: string;
  organization: string;
  email: string;
  calendlyUrl: string;
  headline: string;
  description: string;
  ctaLabel: string;
}

export const CONTACTS: Record<"randy_nemeroff" | "gordon_oliver" | "randy_refi", RoutingContact> = {
  randy_nemeroff: {
    key: "randy_nemeroff",
    name: "Randy Nemeroff",
    title: "Founder, Hero Benefits Alliance",
    organization: "The Hero Benefits Alliance",
    email: "nemeroff@perkplans.com",
    calendlyUrl: "https://calendly.com/nemeroff-herobenefitsalliance",
    headline: "You're ready — let's get you pre-approved.",
    description:
      "Based on your answers, you're in a strong position to move forward. Randy Nemeroff, founder of The Hero Benefits Alliance, will personally walk you through your next steps and confirm the benefits you've earned.",
    ctaLabel: "Book a free consultation",
  },
  randy_refi: {
    key: "randy_refi",
    name: "Randy Nemeroff",
    title: "Founder, Hero Benefits Alliance",
    organization: "The Hero Benefits Alliance",
    email: "nemeroff@perkplans.com",
    calendlyUrl: "https://calendly.com/nemeroff-herobenefitsalliance",
    headline: "Let's see if refinancing makes sense for you.",
    description:
      "As a current homeowner, you may be able to lower your monthly payment, shorten your loan term, or tap into your home's equity. Randy Nemeroff will review your situation and walk you through your refinance options — no cost, no obligation.",
    ctaLabel: "Book a free refi review",
  },
  gordon_oliver: {
    key: "gordon_oliver",
    name: "Gordon Oliver",
    title: "Credit Counselor",
    organization: "Cambridge Credit Counseling",
    email: "goliver@cambridgecredit.org",
    calendlyUrl: "https://calendly.com/golivercambridgecredit",
    headline: "Let's strengthen your financial foundation first.",
    description:
      "Before jumping into the mortgage process, a quick financial tune-up can save you thousands. Gordon Oliver at Cambridge Credit Counseling — a 501(c)(3) nonprofit — will review your situation confidentially and help you improve your credit, reduce debt, and position yourself for the lowest possible rate. This service is completely free and there's no obligation.",
    ctaLabel: "Book a free credit review",
  },
};

/**
 * Extended answers object for smarter routing.
 * Fields are optional for backward compatibility.
 */
export interface RoutingAnswers {
  credit?: string;       // "excellent" | "good" | "fair" | "unknown" | "issues"
  downPayment?: string;  // "ready" | "saving" | "not-started" | "myth"
  blocker?: string;      // "income" | "down-payment" | "credit" | "process" | "timing" | "nothing"
  // LifeMart funnel equivalents
  creditScore?: string;  // "excellent" | "good" | "fair" | "poor" | "not-sure"
  biggestChallenge?: string; // "credit-score" | "understanding-process" | "none-ready" | etc.
}

/**
 * Determine routing based on survey segment, key signals, and full answers.
 */
export function routeLead(
  segment: "ready" | "almost" | "early" | "refi",
  preApprovalAnswer?: string,
  answers?: RoutingAnswers
): RoutingContact {
  // Refi always goes to Randy
  if (segment === "refi") {
    return CONTACTS.randy_refi;
  }

  // Normalize answer fields across both funnels
  const credit = answers?.credit || answers?.creditScore || "";
  const blocker = answers?.blocker || answers?.biggestChallenge || "";
  const downPayment = answers?.downPayment || "";

  // RULE: If blocker is explicitly "credit" or "credit-score", route to Gordon
  if (blocker === "credit" || blocker === "credit-score") {
    return CONTACTS.gordon_oliver;
  }

  // RULE: "Ready" segment always goes to Randy
  if (segment === "ready") {
    return CONTACTS.randy_nemeroff;
  }

  // RULE: Blocker is "process" / "understanding-process" / "nothing" / "none-ready" → education need → Randy
  const educationBlockers = ["process", "understanding-process", "nothing", "none-ready", "timing"];
  if (educationBlockers.includes(blocker)) {
    return CONTACTS.randy_nemeroff;
  }

  // RULE: Pre-approved users go to Randy
  const preApprovedValues = ["yes", "yes-recent", "yes-expired", "in-progress"];
  if (preApprovalAnswer && preApprovedValues.includes(preApprovalAnswer)) {
    return CONTACTS.randy_nemeroff;
  }

  // RULE: "Almost" segment with strong credit + down payment → Randy
  const strongCredit = ["excellent", "good"];
  const strongDP = ["ready", "saving", "over-50k", "30k-50k", "15k-30k"];
  if (segment === "almost" && strongCredit.includes(credit) && strongDP.includes(downPayment)) {
    return CONTACTS.randy_nemeroff;
  }

  // Default: Gordon for early-stage or weak financial signals
  return CONTACTS.gordon_oliver;
}
